import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {Environment} from '../model/environment';
import {CommunicationPreferences} from '../model/patron/communication-preferences';
import {ServiceOptions, ServiceUtilService} from './service-util.service';
import {ResponseBase} from '../model/response-base/response-base';
import {Patron} from '../model/patron/patron';
import {Store} from '@ngrx/store';
import {PatronActions} from '@store/patron/patron.actions';
import {MatDialog} from '@angular/material/dialog';
import {LoginComponent} from '../core/login/login.component';

@Injectable({providedIn: 'root'})
export class PatronService {

  constructor(
    private dialog: MatDialog,
    private environment: Environment,
    private http: HttpClient,
    private serviceUtil: ServiceUtilService,
    private store: Store,
  ) {
  }

  getCommunicationPreferences(): Observable<CommunicationPreferences> {
    const url = `${this.environment.apiUrl}/patrons/v2/patrons/communication-preferences`;
    return this.serviceUtil.httpInvoker(this.http.get<ResponseBase<CommunicationPreferences>>(url))
      .pipe(map(rb => rb.objects[0]));
  }

  updateCommunicationPreferences(preferences: CommunicationPreferences, options?: ServiceOptions<ResponseBase<CommunicationPreferences>>): void {
    const url = `${this.environment.apiUrl}/patrons/v2/patrons/communication-preferences`;
    this.serviceUtil.httpInvoker(this.http.put<ResponseBase<CommunicationPreferences>>(url, JSON.stringify(preferences)), options);
  }

  updatePin(pin: string, options?: ServiceOptions<boolean>): Observable<boolean> {
    const url = `${this.environment.apiUrl}/patrons/v1/patrons/account/pin`;
    const result = this.serviceUtil.httpInvoker(this.http.patch<boolean>(url, pin), options);
    result.subscribe()
    return result;
  }

  save(patron: Patron, options?: ServiceOptions<ResponseBase<Patron>>): Observable<Patron> {
    const url = `${this.environment.apiUrl}/patrons/v2/patrons`;

    const body: PatronUpdatePayload = {
      firstName: patron.firstName,
      middleName: patron.middleName,
      lastName: patron.lastName,
      preferredName: patron.preferredName,
      suffix: patron.suffix,
      notificationEmail: patron.notificationEmail,
      dateOfBirth: patron.dateOfBirth,
      primaryAddress: {
        address: patron.address,
        address2: patron.address2,
        city: patron.city,
        state: patron.state,
        zipCode: patron.zipCode
      },
      phone1: patron.phone1,
      phone1Mobile: patron.phone1Mobile,
      phone1Ext: patron.phone1Ext,
      phone2: patron.phone2,
      phone2Mobile: patron.phone2Mobile,
      phone2Ext: patron.phone2Ext,
      phoneSms: patron.phoneSms,
      phoneCall: patron.phoneCall,
      defaultHoldPickupBranchId: patron.defaultHoldPickupBranchId,
      pronounId: patron.pronouns?.id ?? null,
      optInReadingHistory: patron.optInReadingHistory,
      oneClickHoldsEnabled: patron.oneClickHoldsEnabled
    };

    const result = this.serviceUtil.httpInvoker(this.http.put<ResponseBase<Patron>>(url, body), options)
      .pipe(map(rb => rb.objects[0]));
    result.subscribe(() => this.updatePatronState());
    return result;
  }

  updatePatronState(): void {
    this.store.dispatch(PatronActions.reloadAuthPatron());
  }

  /**
   * Patron self renew expired account in good standing
   */
  renewAccount(options?: ServiceOptions<void>): Observable<void> {
    const url = `${this.environment.apiUrl}/patrons/v1/patrons/account/renew`;
    const result = this.serviceUtil.httpInvoker(this.http.patch<void>(url, ''), options);
    result.subscribe(() => this.updatePatronState()); // to update patron w/ new status + expiration instead of making assumptions about them in component subscription
    return result;
  }

  openLoginDialog(): void {
    this.dialog.open(LoginComponent);
  }
}

interface PatronUpdatePayload {
  readonly firstName: string;
  readonly middleName: string;
  readonly lastName: string;
  readonly preferredName: string;
  readonly suffix: string;

  readonly notificationEmail: string;

  readonly dateOfBirth: string;

  readonly primaryAddress: PatronAddressPayload;

  readonly phone1: string;
  readonly phone1Mobile: boolean;
  readonly phone1Ext: string;

  readonly phone2: string;
  readonly phone2Mobile: boolean;
  readonly phone2Ext: string;

  readonly phoneSms: string;
  readonly phoneCall: string;

  readonly defaultHoldPickupBranchId: number;
  readonly pronounId: number | null;
  readonly optInReadingHistory: boolean;
  readonly oneClickHoldsEnabled: boolean;
}

interface PatronAddressPayload {
  readonly address: string;
  readonly address2: string;
  readonly city: string;
  readonly state: string;
  readonly zipCode: string;
}
